import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { SiteConfigService } from '../../../core/site-config.service';
import { AuthService } from '../../../core/auth.service';
import {
  AppointmentForm,
  AppointmentFormField,
  ShowcaseBranch,
  ShowcaseService
} from './brand-showcase.service';
import {
  AppointmentBookingService,
  BookingCustomerProfile,
  AppointmentResult,
  BookingAvailability,
  BookingService,
  BookingStaff
} from './appointment-booking.service';

type BookingStep = 'branch' | 'date' | 'staff' | 'service' | 'time' | 'profile' | 'verify' | 'success';
type CalendarDay = { date: string; day: number; disabled: boolean };
type AppointmentSlot = { time: string; occupied: boolean };

@Component({
  selector: 'app-appointment-widget',
  imports: [FormsModule],
  template: `
    <section class="appointment-card" id="randevu">
      <header>
        <p>{{ site.text('appointmentEyebrow', 'Randevu') }}</p>
        <h2>{{ site.text('appointmentTitle', 'Sana uygun zamanı birlikte planlayalım.') }}</h2>
        <span>{{ site.text('appointmentLead', 'Şubeni, uzmanını, hizmetini ve uygun saati seçerek randevunu oluştur.') }}</span>
      </header>

      @if (step() !== 'success') {
        <nav class="steps" [attr.aria-label]="site.text('appointmentStepsLabel', 'Randevu adımları')">
          @for (item of stepItems(); track item.key; let index = $index) {
            <button type="button" [class.active]="stepIndex() >= index" [class.current]="step() === item.key"
              [disabled]="index >= stepIndex()" (click)="go(item.key)">
              <small>{{ index + 1 }}</small><span>{{ item.label }}</span>
            </button>
          }
        </nav>
      }

      @if (error()) { <div class="notice error">{{ error() }}</div> }
      @if (loading()) { <div class="notice loading">{{ site.text('appointmentLoading', 'Bilgiler hazırlanıyor...') }}</div> }

      @if (step() === 'branch') {
        <div class="section-head"><strong>{{ site.text('appointmentChooseBranch', 'Şube seç') }}</strong><span>{{ site.text('appointmentChooseBranchLead', 'Sana uygun lokasyonu seç.') }}</span></div>
        <div class="choice-grid">
          @for (branch of branches(); track branch.id) {
            <button type="button" [class.selected]="branchID() === branch.id" (click)="chooseBranch(branch)">
              @if(branch.imageUrl){<img class="branch-image" [src]="branch.imageUrl" [alt]="branch.name"/>}
              <strong>{{ branch.name }}</strong><small>{{ branch.address }}</small>
            </button>
          } @empty { <div class="empty">{{ site.text('appointmentNoBranches', 'Randevuya açık şube bulunamadı.') }}</div> }
        </div>
      }

      @if (step() === 'date') {
        <div class="section-head"><strong>{{ site.text('appointmentChooseDate', 'Tarih seç') }}</strong><span>{{ site.text('appointmentChooseDateLead', 'Uygun personeller seçtiğin güne göre hazırlanır.') }}</span></div>
        <section class="calendar">
          <header>
            <button type="button" aria-label="Önceki tarihler" [disabled]="!canGoPreviousMonth()" (click)="changeMonth(-1)">←</button>
            <strong>{{ calendarTitle() }}</strong>
            <button type="button" aria-label="Sonraki tarihler" (click)="changeMonth(1)">→</button>
          </header>
          <div class="weekdays">@for (day of weekDays(); track day) { <span>{{ day }}</span> }</div>
          <div class="calendar-grid">
            @for (item of calendarDays(); track item.date) {
              <button type="button" [class.selected]="date() === item.date"
                [disabled]="item.disabled" (click)="selectDate(item.date)">{{ item.day }}</button>
            }
          </div>
        </section>
        <div class="actions"><button class="back" type="button" (click)="go('branch')">{{ site.text('back', 'Geri') }}</button><button class="next" type="button" [disabled]="!date() || loading()" (click)="loadStaff()">{{ site.text('appointmentChooseStaff', 'Personel seç') }}</button></div>
      }

      @if (step() === 'staff') {
        <div class="section-head"><strong>{{ site.text('appointmentChooseStaff', 'Personel seç') }}</strong><span>{{ site.text('appointmentChooseStaffLead', 'Bu tarihte randevuya açık ekip üyeleri.') }}</span></div>
        <div class="staff-grid">
          @for (person of staff(); track person.branchStaffID) {
            <button type="button" [disabled]="person.available === false || person.available === 0" (click)="chooseStaff(person)">
              <div>@if (staffImage(person)) { <img [src]="staffImage(person)" [alt]="staffName(person)"/> } @else { <b>{{ initials(staffName(person)) }}</b> }</div>
              <strong>{{ staffName(person) }}</strong><small>{{ person.professionName || person.title || 'Uzman' }}</small>
            </button>
          } @empty { <div class="empty">{{ site.text('appointmentNoStaff', 'Bu tarihte uygun personel bulunamadı. Başka bir tarih deneyebilirsin.') }}</div> }
        </div>
        <div class="actions"><button class="back" type="button" (click)="go('date')">{{ site.text('back', 'Geri') }}</button></div>
      }

      @if (step() === 'service') {
        <div class="section-head"><strong>{{ site.text('appointmentChooseService', 'Hizmet seç') }}</strong><span>{{ site.text('appointmentChooseServiceLead', 'Bir veya birden fazla hizmet seçebilirsin.') }}</span></div>
        <div class="choice-grid services">
          @for (service of staffServices(); track service.id) {
            <button type="button" [class.selected]="isServiceSelected(service.id)" (click)="toggleService(service.id)">
              <strong>{{ service.title }}</strong>
              <small>@if(service.duration){Yaklaşık {{ service.duration }} dk}@if(service.price){ · {{ service.price }} {{ service.currency }}}</small>
            </button>
          } @empty { <div class="empty">{{ site.text('appointmentNoServices', 'Bu personel için randevuya açık hizmet bulunamadı.') }}</div> }
        </div>
        <div class="selection-summary"><span>{{ selectedServiceIDs().length }} hizmet</span><strong>{{ totalDuration() }} dakika</strong></div>
        <div class="actions"><button class="back" type="button" (click)="go('staff')">{{ site.text('back', 'Geri') }}</button><button class="next" type="button" [disabled]="!selectedServiceIDs().length || loading()" (click)="loadAvailability()">{{ site.text('appointmentShowTimes', 'Uygun saatleri gör') }}</button></div>
      }

      @if (step() === 'time') {
        <div class="section-head"><strong>{{ site.text('appointmentChooseTime', 'Saat seç') }}</strong><span>{{ totalDuration() }} {{ site.text('minutesLabel', 'dakikalık') }} {{ site.text('appointmentAvailableStartTimes', 'hizmet için uygun başlangıç saatleri.') }}</span></div>
        <div class="time-grid">
          @for (slot of slots(); track slot.time) {
            <button type="button" [class.selected]="time() === slot.time" [class.occupied]="slot.occupied"
              [disabled]="slot.occupied" (click)="chooseTime(slot.time)">
              <strong>{{ slot.time }}</strong><small>@if(slot.occupied) { {{ site.text('appointmentOccupied', 'Dolu') }} } @else { {{ slotEnd(slot.time) }} bitiş }</small>
            </button>
          }
          @empty { <div class="empty">{{ site.text('appointmentNoTimes', 'Seçtiğin hizmet süresine uygun boş saat bulunamadı.') }}</div> }
        </div>
        <div class="actions"><button class="back" type="button" (click)="go('service')">{{ site.text('back', 'Geri') }}</button></div>
      }

      @if (step() === 'profile') {
        <div class="section-head"><strong>{{ customerProfiles().length && phoneVerified() ? 'Kimin için randevu alıyorsun?' : site.text('appointmentContact', 'İletişim bilgileri') }}</strong><span>{{ phoneVerified() ? 'Doğrulanmış hesabınla tekrar kod girmeden devam edebilirsin.' : site.text('appointmentContactLead', 'Randevunu doğrulamak için gerekli bilgileri doldur.') }}</span></div>
        @if(customerProfiles().length && phoneVerified()){
          <div class="profile-picker">@for(profile of customerProfiles();track profile.profileID){<button type="button" [class.selected]="profileMode()==='existing'&&selectedProfileID()===profile.profileID" (click)="selectProfile(profile)"><i>{{initials(profile.fullName||'Profil')}}</i><span><strong>{{profile.fullName||'Profil'}}</strong><small>{{profile.phone||profile.email||'Kayıtlı profil'}}</small></span><b>✓</b></button>}<button type="button" class="new-profile" [class.selected]="profileMode()==='new'" (click)="newProfile()"><i>＋</i><span><strong>Yeni profil ekle</strong><small>Başka biri veya yeni bilgiler</small></span></button></div>
        }
        @if(profileMode()==='new' || !customerProfiles().length || !phoneVerified()){<div class="form-grid">
          @for (field of appointmentForm().fields; track field.id) {
            <label [class.wide]="isWideField(field)">
              <span>{{ field.title || field.formKey }} @if (required(field)) { <b>*</b> }</span>
              @if (field.type === 'textarea') {
                <textarea rows="4" [ngModel]="fieldValue(field)" (ngModelChange)="setField(field, $event)" [placeholder]="field.description || ''"></textarea>
              } @else if (field.type === 'select') {
                <select [ngModel]="fieldValue(field)" (ngModelChange)="setField(field, $event)">
                  <option value="">{{ site.text('select', 'Seçiniz') }}</option>
                  @for (option of fieldOptions(field); track option.value) { <option [value]="option.value">{{ option.label }}</option> }
                </select>
              } @else if (field.type === 'radio') {
                <div class="radio-list">
                  @for (option of fieldOptions(field); track option.value) {
                    <label><input type="radio" [name]="field.formKey" [value]="option.value" [ngModel]="fieldValue(field)" (ngModelChange)="setField(field, $event)"/><span>{{ option.label }}</span></label>
                  }
                </div>
              } @else if (field.type === 'checkbox' || field.type === 'switch') {
                <input type="checkbox" [ngModel]="fieldValue(field)" (ngModelChange)="setField(field, $event)"/>
              } @else {
                <input [type]="inputType(field.type)" [ngModel]="fieldValue(field)" (ngModelChange)="setField(field, $event)" [minlength]="number(field.minlenght) || null" [maxlength]="number(field.maxlenght) || null" [placeholder]="field.description || ''"/>
              }
            </label>
          } @empty {
            <div class="empty">{{ site.text('appointmentNoForm', 'Randevu iletişim formu bu işletme için henüz tanımlanmamış.') }}</div>
          }
        </div>}
        <div class="actions"><button class="back" type="button" (click)="go('time')">{{ site.text('back', 'Geri') }}</button><button class="next" type="button" [disabled]="!bookingProfileValid() || loading()" (click)="submit()">{{ phoneVerified() ? 'Randevuyu oluştur' : site.text('appointmentSendCode', 'Doğrulama kodu gönder') }}</button></div>
        <small class="consent">{{ phoneVerified() ? 'Telefonun daha önce doğrulandığı için yeniden kod istemeyeceğiz.' : site.text('appointmentVerificationLead', 'Telefonuna gönderilen kodu doğruladıktan sonra randevun oluşturulur.') }}</small>
      }

      @if (step() === 'verify') {
        <div class="verify-card">
          <p>{{ site.text('appointmentCodeSent', 'Doğrulama kodu') }} <strong>{{ maskedPhone() }}</strong> {{ site.text('appointmentCodeSentSuffix', 'numarasına gönderildi.') }}</p>
          <label><span>{{ site.text('appointmentCodeLabel', '6 haneli kod') }}</span><input inputmode="numeric" maxlength="6" [ngModel]="verificationCode()" (ngModelChange)="verificationCode.set($event)"/></label>
        </div>
        <div class="actions"><button class="back" type="button" (click)="go('profile')">{{ site.text('back', 'Geri') }}</button><button class="next" type="button" [disabled]="verificationCode().trim().length < 4 || loading()" (click)="verify()">{{ site.text('appointmentComplete', 'Randevuyu tamamla') }}</button></div>
      }

      @if (step() === 'success') {
        <div class="success-card">
          <i>✓</i><p>{{ site.text('appointmentSuccess', 'Randevun oluşturuldu') }}</p>
          <h3>{{ result()?.appointmentDate }} · {{ result()?.startTime }}</h3>
          @if (result()?.branchName) { <span>{{ result()?.branchName }}</span> }
          @if (result()?.staffName) { <span>{{ result()?.staffName }}</span> }
          @if (result()?.listingNames?.length) { <span>{{ result()?.listingNames?.join(', ') }}</span> }
          @if(result()?.deposit;as deposit){<section class="deposit-box"><small>RANDEVU KAPORASI</small><strong>{{deposit.amount}} {{deposit.currency||'TRY'}}</strong><p>Ödeme referansın: <b>{{deposit.reference}}</b></p>@if(deposit.paymentLink){<a [href]="deposit.paymentLink" target="_blank" rel="noopener">Kartla güvenli öde</a><span>Aymudo Mirror · %{{deposit.commissionRate||3}} komisyon</span>}@if(deposit.bankTransferEnabled){<div><b>{{deposit.bankName}}</b><span>{{deposit.accountHolder}}</span><code>{{deposit.iban}}</code><p>Havale açıklamasına <strong>{{deposit.reference}}</strong> yazarak gönderebilirsin.</p></div>}</section>}
          <nav>
            @if (result()?.manageUrl) { <a [href]="result()?.manageUrl">Randevumu yönet</a> }
            @if (result()?.branchMapsUrl) { <a [href]="result()?.branchMapsUrl" target="_blank" rel="noopener">Yol tarifi</a> }
            @if (result()?.googleCalendarUrl) { <a [href]="result()?.googleCalendarUrl" target="_blank" rel="noopener">Takvime ekle</a> }
            <button type="button" (click)="reset()">{{ site.text('appointmentNew', 'Yeni randevu') }}</button>
          </nav>
        </div>
      }
    </section>
  `,
  styles: [`
    .appointment-card{background:var(--ink);border-radius:40px;color:var(--paper);display:grid;gap:24px;padding:clamp(24px,5vw,58px)}header{display:grid;gap:10px;max-width:900px}header p{color:var(--accent);font-size:12px;font-weight:950;letter-spacing:.15em;margin:0;text-transform:uppercase}h2{font-family:Georgia,serif;font-size:clamp(2rem,5vw,4.6rem);letter-spacing:-.06em;line-height:.98;margin:0}header span,.consent{color:color-mix(in srgb,var(--paper) 68%,transparent);line-height:1.6}
    .steps{display:flex;gap:8px;overflow:auto;padding:2px 0 7px;scrollbar-width:none}.steps button{align-items:center;background:transparent;border:1px solid color-mix(in srgb,var(--paper) 18%,transparent);border-radius:999px;color:color-mix(in srgb,var(--paper) 48%,transparent);display:flex;flex:0 0 auto;gap:8px;padding:8px 14px 8px 9px}.steps button small{align-items:center;border-radius:50%;display:flex;font-weight:950;height:25px;justify-content:center;width:25px}.steps button span{font-size:.78rem;font-weight:850;white-space:nowrap}.steps button.active{border-color:color-mix(in srgb,var(--accent) 55%,transparent);color:var(--accent)}.steps button.current{background:color-mix(in srgb,var(--accent) 16%,transparent);border-color:var(--accent);color:var(--paper)}.steps button.current small{background:var(--accent);color:var(--ink)}.steps button:disabled{opacity:1}.section-head{display:grid;gap:5px}.section-head strong{font-size:1.25rem}.section-head span{color:color-mix(in srgb,var(--paper) 62%,transparent)}
    .choice-grid,.staff-grid{display:grid;gap:12px;grid-template-columns:repeat(2,minmax(0,1fr))}.choice-grid.services{grid-template-columns:repeat(3,minmax(0,1fr))}.choice-grid button,.staff-grid button{background:color-mix(in srgb,var(--paper) 7%,transparent);border:1px solid color-mix(in srgb,var(--paper) 15%,transparent);border-radius:20px;color:var(--paper);display:grid;gap:7px;padding:18px;text-align:left}.choice-grid button.selected{background:color-mix(in srgb,var(--accent) 20%,transparent);border-color:var(--accent)}.choice-grid small,.staff-grid small{color:color-mix(in srgb,var(--paper) 65%,transparent);line-height:1.45}.staff-grid{grid-template-columns:repeat(4,minmax(0,1fr))}.staff-grid button{text-align:center}.staff-grid button>div{align-items:center;aspect-ratio:1;background:color-mix(in srgb,var(--accent) 14%,transparent);border-radius:18px;display:flex;justify-content:center;overflow:hidden}.staff-grid img{height:100%;object-fit:cover;width:100%}.staff-grid b{font-size:2rem}
    .actions{align-items:center;background:color-mix(in srgb,var(--ink) 92%,transparent);border:1px solid color-mix(in srgb,var(--paper) 14%,transparent);border-radius:18px;bottom:12px;box-shadow:0 12px 28px rgba(0,0,0,.2);display:flex;gap:10px;justify-content:flex-end;padding:10px;position:sticky;backdrop-filter:blur(14px);z-index:10}.next,.back,.success-card button,.success-card a{align-items:center;border:0;border-radius:999px;display:inline-flex;font-weight:950;justify-content:center;min-height:50px;padding:0 22px}.next,.success-card a{background:var(--accent);color:var(--ink)}.back,.success-card button{background:transparent;border:1px solid color-mix(in srgb,var(--paper) 24%,transparent);color:var(--paper)}button:disabled,.next:disabled{opacity:.4;pointer-events:none}.selection-summary{border-top:1px solid color-mix(in srgb,var(--paper) 15%,transparent);display:flex;justify-content:space-between;padding-top:14px}
    .calendar{background:color-mix(in srgb,var(--paper) 6%,transparent);border:1px solid color-mix(in srgb,var(--paper) 16%,transparent);border-radius:26px;display:grid;gap:14px;max-width:620px;padding:18px}.calendar header{align-items:center;display:grid;grid-template-columns:42px 1fr 42px;max-width:none;text-align:center}.calendar header button{background:transparent;border:1px solid color-mix(in srgb,var(--paper) 18%,transparent);border-radius:50%;color:var(--paper);height:42px}.calendar header button:disabled{opacity:.25}.calendar header strong{text-transform:capitalize}.weekdays,.calendar-grid{display:grid;gap:6px;grid-template-columns:repeat(7,1fr)}.weekdays span{color:color-mix(in srgb,var(--paper) 50%,transparent);font-size:.68rem;font-weight:900;text-align:center;text-transform:uppercase}.calendar-grid button{aspect-ratio:1;background:transparent;border:1px solid transparent;border-radius:13px;color:var(--paper);font-weight:850;min-width:0}.calendar-grid button:hover:not(:disabled){border-color:var(--accent)}.calendar-grid button.outside{color:color-mix(in srgb,var(--paper) 30%,transparent)}.calendar-grid button.selected{background:var(--accent);color:var(--ink)}.calendar-grid button:disabled{color:color-mix(in srgb,var(--paper) 16%,transparent)}.verify-card label{display:grid;gap:8px;max-width:440px}.verify-card input,.form-grid input,.form-grid textarea,.form-grid select{background:color-mix(in srgb,var(--paper) 8%,transparent);border:1px solid color-mix(in srgb,var(--paper) 18%,transparent);border-radius:16px;color:var(--paper);font:inherit;padding:14px}.time-grid{display:grid;gap:10px;grid-template-columns:repeat(4,minmax(0,1fr));max-width:760px}.time-grid button{background:transparent;border:1px solid color-mix(in srgb,var(--paper) 20%,transparent);border-radius:16px;color:var(--paper);display:grid;gap:3px;padding:13px}.time-grid button small{color:color-mix(in srgb,var(--paper) 55%,transparent);font-size:.68rem}.time-grid button.selected{background:var(--accent);color:var(--ink)}.time-grid button.selected small{color:color-mix(in srgb,var(--ink) 65%,transparent)}
    .form-grid{display:grid;gap:14px;grid-template-columns:repeat(2,minmax(0,1fr))}.form-grid>label{display:grid;gap:7px}.form-grid>label.wide{grid-column:1/-1}.form-grid>label>span,.verify-card label span{font-size:12px;font-weight:900;text-transform:uppercase}.form-grid b{color:var(--accent)}.form-grid select option{color:#111}.radio-list{display:flex;flex-wrap:wrap;gap:8px}.radio-list label{align-items:center;border:1px solid color-mix(in srgb,var(--paper) 18%,transparent);border-radius:999px;display:flex;gap:7px;padding:10px 14px}.radio-list label span{text-transform:none}.notice{border-radius:14px;padding:12px 15px}.notice.error{background:#8a2134}.notice.loading{background:color-mix(in srgb,var(--accent) 18%,transparent)}.empty{border:1px dashed color-mix(in srgb,var(--paper) 22%,transparent);border-radius:18px;color:color-mix(in srgb,var(--paper) 62%,transparent);padding:20px}.verify-card{display:grid;gap:18px}.time-grid button.occupied{border-color:#a96868;color:color-mix(in srgb,var(--paper) 48%,transparent);cursor:not-allowed}.time-grid button.occupied small{color:#d28b8b;font-weight:900}.success-card{align-items:center;display:grid;gap:12px;justify-items:center;padding:20px;text-align:center}.success-card>i{align-items:center;background:var(--accent);border-radius:50%;color:var(--ink);display:flex;font-size:2rem;font-style:normal;height:70px;justify-content:center;width:70px}.success-card p{font-weight:950;margin:0}.success-card h3{font-family:Georgia,serif;font-size:2rem;margin:0}.success-card>span{color:color-mix(in srgb,var(--paper) 70%,transparent)}.success-card nav{display:flex;flex-wrap:wrap;gap:9px;justify-content:center;margin-top:10px}
    .branch-image{aspect-ratio:16/7;border-radius:14px;object-fit:cover;width:100%}.profile-picker{display:grid;gap:10px;grid-template-columns:repeat(2,minmax(0,1fr))}.profile-picker button{align-items:center;background:color-mix(in srgb,var(--paper) 7%,transparent);border:1px solid color-mix(in srgb,var(--paper) 16%,transparent);border-radius:19px;color:var(--paper);display:grid;gap:12px;grid-template-columns:auto 1fr auto;padding:14px;text-align:left}.profile-picker button.selected{background:color-mix(in srgb,var(--accent) 18%,transparent);border-color:var(--accent)}.profile-picker i{align-items:center;background:color-mix(in srgb,var(--accent) 18%,transparent);border-radius:50%;display:flex;font-style:normal;font-weight:950;height:44px;justify-content:center;width:44px}.profile-picker span{display:grid;gap:3px}.profile-picker small{color:color-mix(in srgb,var(--paper) 62%,transparent)}.profile-picker b{color:var(--accent);opacity:0}.profile-picker .selected b{opacity:1}.deposit-box{background:color-mix(in srgb,var(--accent) 12%,var(--ink));border:1px solid color-mix(in srgb,var(--accent) 50%,transparent);border-radius:22px;display:grid;gap:8px;max-width:620px;padding:20px;text-align:left;width:100%}.deposit-box>strong{color:var(--accent);font-size:2rem}.deposit-box p{margin:0}.deposit-box>a{justify-self:start}.deposit-box div{border-top:1px solid color-mix(in srgb,var(--paper) 15%,transparent);display:grid;gap:6px;margin-top:6px;padding-top:12px}.deposit-box code{background:color-mix(in srgb,var(--paper) 8%,transparent);border-radius:9px;font-size:1rem;padding:9px;word-break:break-all}
    @media(max-width:900px){.staff-grid{grid-template-columns:repeat(3,1fr)}}@media(max-width:760px){.choice-grid,.choice-grid.services,.form-grid{grid-template-columns:1fr}.appointment-card{border-radius:28px}.actions{justify-content:stretch}.actions>*{flex:1}.staff-grid{grid-template-columns:repeat(2,1fr)}.time-grid{grid-template-columns:repeat(2,1fr)}.steps{margin-inline:-4px}.calendar{padding:13px}.calendar-grid button{border-radius:10px}} 
  `]
})
export class AppointmentWidgetComponent {
  protected readonly site = inject(SiteConfigService);
  protected readonly auth = inject(AuthService);
  private readonly booking = inject(AppointmentBookingService);
  private readonly route = inject(ActivatedRoute);
  readonly branches = input<ShowcaseBranch[]>([]);
  readonly services = input<ShowcaseService[]>([]);
  readonly appointmentForm = input<AppointmentForm>({ formID: 0, fields: [] });
  private autoBranchStarted = false;
  private pendingDate='';
  private pendingStaffID=0;
  private pendingServiceID=0;

  protected readonly stepItems = computed(() => [
    { key: 'branch' as BookingStep, label: this.site.text('appointmentStepBranch', 'Şube') },
    { key: 'date' as BookingStep, label: this.site.text('appointmentStepDate', 'Tarih') },
    { key: 'staff' as BookingStep, label: this.site.text('appointmentStepStaff', 'Uzman') },
    { key: 'service' as BookingStep, label: this.site.text('appointmentStepService', 'Hizmet') },
    { key: 'time' as BookingStep, label: this.site.text('appointmentStepTime', 'Saat') },
    { key: 'profile' as BookingStep, label: this.site.text('appointmentStepProfile', 'Bilgiler') },
    { key: 'verify' as BookingStep, label: this.site.text('appointmentStepVerify', 'Doğrula') }
  ]);
  protected readonly step = signal<BookingStep>('branch');
  protected readonly stepIndex = computed(() => Math.max(0, this.stepItems().findIndex((item) => item.key === this.step())));
  protected readonly loading = signal(false);
  protected readonly error = signal('');
  protected readonly branchID = signal(0);
  protected readonly draftID = signal(0);
  protected readonly date = signal('');
  protected readonly staff = signal<BookingStaff[]>([]);
  protected readonly branchStaffID = signal(0);
  protected readonly staffServices = signal<BookingService[]>([]);
  protected readonly selectedServiceIDs = signal<number[]>([]);
  protected readonly availability = signal<BookingAvailability | null>(null);
  protected readonly slots = signal<AppointmentSlot[]>([]);
  protected readonly time = signal('');
  protected readonly formValues = signal<Record<string, unknown>>({});
  protected readonly temporaryID = signal(0);
  protected readonly maskedPhone = signal('');
  protected readonly verificationCode = signal('');
  protected readonly result = signal<AppointmentResult | null>(null);
  protected readonly customerProfiles = signal<BookingCustomerProfile[]>([]);
  protected readonly phoneVerified = signal(false);
  protected readonly selectedProfileID = signal(0);
  protected readonly profileMode = signal<'existing'|'new'>('new');
  protected readonly minimumDate = signal(this.localDate(new Date()));
  protected readonly calendarMonth = signal(new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()));
  protected readonly branchWorkingHours = signal<Record<string, Array<{ startTime: string; endTime: string; isClosed?: boolean | number }>>>({});
  protected readonly weekDays = computed(() => Array.from({ length: 7 }, (_, index) => {
    const start = this.calendarMonth();
    const value = new Date(start.getFullYear(), start.getMonth(), start.getDate() + index);
    return new Intl.DateTimeFormat('tr-TR', { weekday: 'short' }).format(value).replace('.', '');
  }));
  protected readonly calendarTitle = computed(() => {
    const start = this.calendarMonth();
    const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 41);
    const format = (value: Date) => new Intl.DateTimeFormat('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }).format(value);
    return `${format(start)} – ${format(end)}`;
  });
  protected readonly calendarDays = computed<CalendarDay[]>(() => {
    const start = this.calendarMonth();
    return Array.from({ length: 42 }, (_, index) => {
      const value = new Date(start.getFullYear(), start.getMonth(), start.getDate() + index);
      const date = this.localDate(value);
      return {
        date,
        day: value.getDate(),
        disabled: date < this.minimumDate() || !this.branchOpenOn(value)
      };
    });
  });
  protected readonly totalDuration = computed(() => {
    const selected = new Set(this.selectedServiceIDs());
    return this.staffServices().filter((item) => selected.has(Number(item.id))).reduce((sum, item) => sum + Number(item.duration || 0), 0);
  });

  constructor() {
    const params=this.route.snapshot.queryParamMap;
    this.pendingDate=params.get('date') || '';
    this.pendingStaffID=Number(params.get('branchStaffID') || 0);
    this.pendingServiceID=Number(params.get('serviceID') || 0);
    const requestedBranch=Number(params.get('branchID') || 0);
    effect(() => {
      const branches = this.branches();
      const branch=branches.find(item=>Number(item.id)===requestedBranch) || (branches.length===1 ? branches[0] : null);
      if (!this.autoBranchStarted && branch && this.step() === 'branch' && !this.loading()) {
        this.autoBranchStarted = true;
        void this.chooseBranch(branch);
      }
    });
  }

  protected async chooseBranch(branch: ShowcaseBranch) {
    let continueFromDeepLink = false;
    await this.run(async () => {
      const started = await this.booking.start(branch.id);
      if (!started.draftID) throw new Error('Randevu akışı başlatılamadı.');
      this.branchID.set(branch.id);
      this.draftID.set(started.draftID);
      this.branchWorkingHours.set(started.workingHours || {});
      this.alignCalendarWindow();
      this.step.set('date');
      if(this.pendingDate && this.pendingDate>=this.minimumDate() && this.branchOpenOn(new Date(`${this.pendingDate}T00:00:00`))){
        this.date.set(this.pendingDate);
        continueFromDeepLink = true;
      }
    });
    // loadStaff has its own loading guard. Run it after branch creation finishes so
    // preselected date/staff/service links can advance all the way to availability.
    if(continueFromDeepLink){ await this.loadStaff(); }
  }

  protected async loadStaff() {
    await this.run(async () => {
      const staff = await this.booking.staff(this.draftID(), this.branchID(), this.date());
      const available = staff.filter((person) => person.available !== false && person.available !== 0);
      this.staff.set(available);
      const requested=available.find(person=>Number(person.branchStaffID)===this.pendingStaffID);
      if(requested){ await this.prepareStaff(requested); return; }
      if (available.length === 1) {
        await this.prepareStaff(available[0]);
      } else {
        this.step.set('staff');
      }
    });
  }

  protected async chooseStaff(person: BookingStaff) {
    await this.run(async () => {
      await this.prepareStaff(person);
    });
  }

  protected toggleService(id: number) {
    this.selectedServiceIDs.update((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  }

  protected isServiceSelected(id: number) {
    return this.selectedServiceIDs().includes(id);
  }

  protected async loadAvailability() {
    await this.run(async () => {
      const availability = await this.booking.availability(
        this.draftID(), this.branchID(), this.date(), this.branchStaffID(), this.selectedServiceIDs()
      );
      if (!availability) throw new Error('Uygun saat bilgisi alınamadı.');
      this.availability.set(availability);
      this.slots.set(this.buildSlots(availability));
      this.step.set('time');
    });
  }

  protected async chooseTime(time: string) {
    if (this.slots().find((slot) => slot.time === time)?.occupied) return;
    await this.run(async () => {
      if (!await this.booking.selectTime(this.draftID(), this.date(), time)) throw new Error('Saat seçilemedi.');
      this.time.set(time);
      await this.auth.refresh();
      if(this.auth.loggedIn()){
        const account=await this.booking.customerProfiles();this.customerProfiles.set(account.profiles);this.phoneVerified.set(account.phoneVerified);
        if(account.phoneVerified&&account.profiles.length){this.selectedProfileID.set(Number(account.profiles[0].profileID));this.profileMode.set('existing')}else this.profileMode.set('new');
      }
      this.step.set('profile');
    });
  }

  protected async submit() {
    if (!this.bookingProfileValid()) return;
    await this.run(async () => {
      const useExisting=this.profileMode()==='existing'&&this.selectedProfileID()>0;
      const response = await this.booking.submit({
        branchID: this.branchID(),
        branchStaff: this.branchStaffID(),
        branchStaffID: this.branchStaffID(),
        date: this.date(),
        time: this.time(),
        listings: this.selectedServiceIDs(),
        profileID: useExisting ? this.selectedProfileID() : undefined,
        profileRegisterType: useExisting ? undefined : 'appointment',
        fastAppointment: useExisting ? {} : this.formValues()
      });
      if(response.completed){this.result.set(response.completed);this.step.set('success');return;}
      if (!response.verification?.temporaryID) throw new Error(response.message || 'Doğrulama kodu gönderilemedi.');
      this.temporaryID.set(response.verification.temporaryID);
      this.maskedPhone.set(response.verification.maskedPhone || '');
      this.step.set('verify');
    });
  }

  protected async verify() {
    await this.run(async () => {
      const response = await this.booking.submit({
        temporaryID: this.temporaryID(),
        verificationCode: this.verificationCode().trim()
      });
      if (!response.completed) throw new Error(response.message || 'Doğrulama kodu geçersiz.');
      this.result.set(response.completed);
      this.step.set('success');
    });
  }

  protected go(step: BookingStep) {
    const targetIndex = this.stepItems().findIndex((item) => item.key === step);
    if (targetIndex > this.stepIndex()) return;
    this.error.set('');
    this.step.set(step);
  }

  protected async selectDate(value: string) {
    this.date.set(value);
    this.staff.set([]);
    this.branchStaffID.set(0);
    this.staffServices.set([]);
    this.selectedServiceIDs.set([]);
    this.slots.set([]);
    await this.loadStaff();
  }

  protected changeMonth(offset: number) {
    const current = this.calendarMonth();
    const next = new Date(current.getFullYear(), current.getMonth(), current.getDate() + offset * 42);
    const minimum = new Date(`${this.minimumDate()}T00:00:00`);
    this.calendarMonth.set(next < minimum ? minimum : next);
  }

  protected canGoPreviousMonth() {
    return this.localDate(this.calendarMonth()) > this.minimumDate();
  }

  protected slotEnd(slot: string) {
    return this.formatTime(this.seconds(slot) + this.totalDuration() * 60);
  }

  protected reset() {
    this.branchID.set(0); this.draftID.set(0); this.date.set(''); this.staff.set([]);
    this.branchStaffID.set(0); this.staffServices.set([]); this.selectedServiceIDs.set([]);
    this.availability.set(null); this.slots.set([]); this.time.set(''); this.formValues.set({});
    this.temporaryID.set(0); this.maskedPhone.set(''); this.verificationCode.set(''); this.result.set(null);
    this.customerProfiles.set([]);this.phoneVerified.set(false);this.selectedProfileID.set(0);this.profileMode.set('new');
    this.branchWorkingHours.set({});
    const today = new Date(); this.minimumDate.set(this.localDate(today)); this.calendarMonth.set(today);
    this.autoBranchStarted = false;
    this.step.set('branch');
  }

  protected staffName(person: BookingStaff) {
    return `${person.name || ''} ${person.surname || ''}`.trim() || person.title || 'Uzman';
  }

  protected staffImage(person: BookingStaff) {
    const raw = String(person.imageUrl || person.media || '');
    if (!raw) return '';
    const path = raw.replace('[size]', 's').replace(/^\/+/, '');
    return /^https?:\/\//.test(path) ? path : `${this.site.config().apiBaseUrl}/${path}`;
  }

  protected initials(name: string) {
    return name.split(/\s+/).slice(0, 2).map((item) => item[0]).join('').toUpperCase();
  }

  protected fieldValue(field: AppointmentFormField) {
    return this.formValues()[this.fieldKey(field)] ?? (field.type === 'checkbox' || field.type === 'switch' ? false : '');
  }

  protected setField(field: AppointmentFormField, value: unknown) {
    this.formValues.update((current) => ({ ...current, [this.fieldKey(field)]: value }));
  }

  protected fieldOptions(field: AppointmentFormField): Array<{ value: string; label: string }> {
    const options = Array.isArray(field.options) ? field.options : [];
    return options.map((option) => typeof option === 'string'
      ? { value: option, label: option }
      : { value: String(option.value ?? option.title ?? option.label ?? ''), label: String(option.label ?? option.title ?? option.value ?? '') }
    ).filter((option) => option.value !== '');
  }

  protected required(field: AppointmentFormField) {
    return field.required === true || field.required === 1 || String(field.required).toLowerCase() === 'true';
  }

  protected profileValid() {
    const fields = this.appointmentForm().fields;
    if (!fields.length) return false;
    return fields.every((field) => {
      const value = this.fieldValue(field);
      const text = typeof value === 'string' ? value.trim() : value;
      if (this.required(field) && (text === '' || text === null || text === undefined || text === false)) return false;
      const length = String(text ?? '').length;
      const min = this.number(field.minlenght);
      const max = this.number(field.maxlenght);
      return (!min || !length || length >= min) && (!max || length <= max);
    });
  }

  protected bookingProfileValid(){return this.profileMode()==='existing'&&this.selectedProfileID()>0&&this.phoneVerified() ? true : this.profileValid()}
  protected selectProfile(profile:BookingCustomerProfile){this.selectedProfileID.set(Number(profile.profileID));this.profileMode.set('existing')}
  protected newProfile(){this.selectedProfileID.set(0);this.profileMode.set('new')}

  protected inputType(type: string) {
    const normalized = String(type || '').toLowerCase();
    if (['email', 'tel', 'date', 'time', 'number'].includes(normalized)) return normalized;
    if (normalized === 'phone') return 'tel';
    if (normalized === 'datetime') return 'datetime-local';
    return 'text';
  }

  protected isWideField(field: AppointmentFormField) {
    return ['textarea', 'radio'].includes(String(field.type).toLowerCase());
  }

  protected number(value: unknown) {
    return Math.max(0, Number(value || 0));
  }

  private fieldKey(field: AppointmentFormField) {
    return String(field.formKey || `field_${field.id}`);
  }

  private buildSlots(availability: BookingAvailability): AppointmentSlot[] {
    const date = new Date(`${this.date()}T00:00:00`);
    const day = date.getDay() || 7;
    const periods = availability.workingHours?.[String(day)] ?? [];
    const duration = availability.listings.reduce((sum, item) => sum + Number(item.duration || 0), 0) || this.totalDuration();
    const occupied = availability.appointments ?? [];
    const slots: AppointmentSlot[] = [];
    for (const period of periods) {
      let start = this.seconds(period.startTime);
      const end = this.seconds(period.endTime);
      while (start + duration * 60 <= end) {
        const candidateEnd = start + duration * 60;
        const overlaps = occupied.some((item) => start < this.seconds(item.endTime) && candidateEnd > this.seconds(item.startTime));
        const candidate = this.formatTime(start);
        const isPast = this.date() === this.localDate(new Date()) && start <= this.seconds(new Date().toTimeString().slice(0, 5));
        if (!isPast) slots.push({ time: candidate, occupied: overlaps });
        start += 15 * 60;
      }
    }
    return slots.filter((slot, index, list) => list.findIndex((item) => item.time === slot.time) === index);
  }

  private async prepareStaff(person: BookingStaff) {
    const branchStaffID = Number(person.branchStaffID || 0);
    const services = await this.booking.services(this.draftID(), this.branchID(), this.date(), branchStaffID);
    this.branchStaffID.set(branchStaffID);
    this.staffServices.set(services);
    this.selectedServiceIDs.set([]);
    const requestedService=services.find(item=>Number(item.id)===this.pendingServiceID);
    if(requestedService){
      this.selectedServiceIDs.set([Number(requestedService.id)]);
      const availability=await this.booking.availability(this.draftID(),this.branchID(),this.date(),branchStaffID,this.selectedServiceIDs());
      if(!availability) throw new Error('Uygun saat bilgisi alınamadı.');
      this.availability.set(availability); this.slots.set(this.buildSlots(availability)); this.step.set('time'); return;
    }
    if (services.length === 1) {
      this.selectedServiceIDs.set([Number(services[0].id)]);
      const availability = await this.booking.availability(
        this.draftID(), this.branchID(), this.date(), branchStaffID, this.selectedServiceIDs()
      );
      if (!availability) throw new Error('Uygun saat bilgisi alınamadı.');
      this.availability.set(availability);
      this.slots.set(this.buildSlots(availability));
      this.step.set('time');
      return;
    }
    this.step.set('service');
  }

  private seconds(value: string) {
    const [hours, minutes] = String(value || '00:00').split(':').map(Number);
    return (hours || 0) * 3600 + (minutes || 0) * 60;
  }

  private formatTime(value: number) {
    return `${String(Math.floor(value / 3600)).padStart(2, '0')}:${String(Math.floor((value % 3600) / 60)).padStart(2, '0')}`;
  }

  private branchOpenOn(value: Date) {
    const schedule = this.branchWorkingHours();
    if (!Object.keys(schedule).length) return true;
    const day = String(value.getDay() || 7);
    const periods = schedule[day] || [];
    const today = new Date();
    const isToday = this.localDate(value) === this.localDate(today);
    const now = this.seconds(today.toTimeString().slice(0, 5));
    return periods.some((period) => {
      if (period.isClosed === true || Number(period.isClosed || 0) === 1) return false;
      const start = this.seconds(period.startTime);
      const end = this.seconds(period.endTime);
      return end > start && (!isToday || end > now);
    });
  }

  private alignCalendarWindow() {
    const today = new Date();
    let first = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    for (let offset = 0; offset < 62; offset++) {
      const candidate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + offset);
      if (this.branchOpenOn(candidate)) {
        first = candidate;
        break;
      }
    }
    const firstDate = this.localDate(first);
    this.minimumDate.set(firstDate);
    this.calendarMonth.set(first);
    if (this.date() && this.date() < firstDate) this.date.set('');
  }

  private localDate(value: Date) {
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
  }

  private async run(action: () => Promise<void>) {
    if (this.loading()) return;
    this.loading.set(true);
    this.error.set('');
    try {
      await action();
    } catch (error) {
      this.error.set(error instanceof Error ? error.message : 'İşlem tamamlanamadı.');
    } finally {
      this.loading.set(false);
    }
  }
}
